import { useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  Input,
  DateInput as DateField,
  Checkbox,
  PhoneInput,
  GoogleInput,
  ZipInput,
} from '@/lib/pages/forms/components';

import {
  glacierNewGradApiSaveAnswersMutation,
  glacierNewGradApiGetAnswersOptions,
  glacierNewGradApiGetAnswersQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { getFormattedGooglePlaceResults } from '@/lib/pages/forms/helpers';

import { Info } from 'lucide-react';
import {
  SelectablePracticeSpecialty,
  SelectableYesNo,
  SelectableUnitedStates,
} from '@/lib/pages/forms/fields';

import { Form } from '@/components/ui/form';

import type { YourInformationForm } from './types';
import {
  DefaultValues,
  detailsFrontToBack,
  detailsBackToFront,
  selectableMonth,
  isRequired,
} from './types';

import type { UsStateEnum, ValidatedAgencyResponse } from '@/lib/heyapi';
import { YesNoEnum } from '@/lib/heyapi';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { PermissionsContext } from '@/lib/layout/PermissionContext';
import { useParams, useNavigate, useOutletContext } from 'react-router';
import { Button } from '@/components/ui/button';

const NewGrad = () => {
  const { pk, agency_id: paramAgencyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { agencyData, validateAgency } = useOutletContext<{
    agencyData: {
      name: string;
      phone: string;
      email: string;
      agencyUuid: string;
      agencyOperatingStates: UsStateEnum[];
    };
    validateAgency: any;
  }>();

  const { data } = useQuery({
    ...glacierNewGradApiGetAnswersOptions({
      path: {
        account_id: pk!,
      },
    }),
    enabled: !!pk,
  });

  const answersQK = glacierNewGradApiGetAnswersQueryKey({
    path: {
      account_id: pk!,
    },
  });

  const form = useForm<YourInformationForm>({
    defaultValues: DefaultValues,
    values: detailsBackToFront(data?.pages.yourInformation),
  });

  const selectableStates = useMemo(() => {
    if (
      paramAgencyId === agencyData.agencyUuid &&
      agencyData.agencyOperatingStates?.length
    ) {
      // subset of SelectableUnitedStates with agency operating states
      return SelectableUnitedStates.filter((state) =>
        agencyData.agencyOperatingStates.includes(state.value)
      );
    }

    return SelectableUnitedStates;
  }, [paramAgencyId, agencyData.agencyUuid, agencyData.agencyOperatingStates]);

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { isDirty, errors },
  } = form;

  const watchPolicyContractState = watch('policyContractState');
  const watchFirstTimePractice = watch('firstTimePractice');

  const saveAnswers = useMutation({
    ...glacierNewGradApiSaveAnswersMutation(),
    onError: (error) => {
      toast({
        title: 'Error saving Policy Details',
        description: (
          <p className="mt-2 max-h-[40vh] w-[340px] rounded-md bg-red-600 p-4 text-white">
            {JSON.stringify(error)}
          </p>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: answersQK });

      if (_data.accountId) {
        navigate(
          `/newgrad${_data.accountId ? `/${_data.accountId}` : ''}/2/${_data.pages.yourInformation.agencyUuid || ''}`
        );
      }

      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Changes saved successfully</code>
          </pre>
        ),
      });
    },
  });

  useEffect(() => {
    setValue('agencyName', agencyData?.name);
    setValue('agencyUuid', agencyData?.agencyUuid);
  }, [agencyData]);

  const handleSelectPracticeState = (e: google.maps.places.PlaceResult) => {
    const { city, county, line1, line2, state, zipCode, formatted } =
      getFormattedGooglePlaceResults(e);

    const dirtyObj = {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    };

    if (line1) {
      setValue('primaryPracticeAddress.line1', line1, dirtyObj);
    }

    if (line2) {
      setValue('primaryPracticeAddress.line2', line2, dirtyObj);
    }

    if (city) {
      setValue('primaryPracticeAddress.city', city, dirtyObj);
    }

    if (county) {
      setValue('primaryPracticeAddress.county', county, dirtyObj);
    } else {
      setValue('primaryPracticeAddress.county', '', dirtyObj);
    }

    if (state) {
      setValue('primaryPracticeAddress.state', state, dirtyObj);
    }

    if (zipCode) {
      setValue('primaryPracticeAddress.zipCode', zipCode, dirtyObj);
    }
  };

  useEffect(() => {
    const state = watchPolicyContractState || null;
    const agencyId =
      paramAgencyId || data?.pages.yourInformation.agencyUuid || null;
    const contractState =
      data?.pages.yourInformation.policyContractState || null;

    if (agencyId || (state && contractState !== state)) {
      validateAgency.mutate({
        body: {
          agencyUuid: agencyId,
          practiceState: state,
        },
      });
    }
  }, [watchPolicyContractState, isDirty]);

  const onSubmit: SubmitHandler<YourInformationForm> = async (formData) => {
    let isError = false;
    let errorMessage = 'Missing required questions';
    const exclude: Array<keyof YourInformationForm> = [
      'professionalDesignation',
      'line2',
      'county',
    ];

    // Primary Practice Location must be in Primary Practice State
    if (
      formData.policyContractState &&
      formData.primaryPracticeAddress.state &&
      formData.primaryPracticeAddress.state !== formData.policyContractState
    ) {
      setError('primaryPracticeAddress.state', {
        type: 'server',
        message: '',
      });
      setError('policyContractState', {
        type: 'server',
        message: '',
      });
      isError = true;
      errorMessage =
        'Primary Practice Location must be in Primary Practice State';
    }

    // Check if any required fields are missing values
    (Object.entries(formData) as [keyof YourInformationForm, any][]).forEach(
      ([key, value]) => {
        if (value !== null && typeof value === 'object') {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            if (
              isRequired(key) &&
              typeof formData[key as keyof YourInformationForm] === 'object' &&
              !formData[key as keyof YourInformationForm][nestedKey]
            ) {
              if (
                key !== 'professionalDesignation' &&
                !exclude.includes(key) &&
                !exclude.includes(nestedKey)
              ) {
                // skip professionalDesignation;
                setError(`${key}.${nestedKey}`, {
                  type: 'server',
                  message: '',
                });
                isError = true;
                errorMessage = 'Missing required questions';
              }
            }
          });
        }
      }
    );

    (Object.keys(formData) as (keyof YourInformationForm)[]).forEach((key) => {
      if (
        isRequired(key) &&
        !formData[key as keyof YourInformationForm] &&
        !exclude.includes(key)
      ) {
        setError(key, {
          type: 'server',
          message: '',
        });
        isError = true;
        errorMessage = 'Missing required questions';
      }
    });

    const isAnyDesignationSelected = Object.values(
      formData.professionalDesignation
    ).some((key) => key);

    if (!isAnyDesignationSelected) {
      setError('professionalDesignation', {
        type: 'server',
        message: '',
      });
      isError = true;
      errorMessage = 'Missing required questions';
    }

    if (isError) {
      // Popup
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="flex w-full whitespace-normal text-white">
              {errorMessage}
            </code>
          </pre>
        ),
      });
    } else {
      saveAnswers.mutate({
        body: {
          accountId: pk || null,
          pages: {
            ...data?.pages,
            yourInformation: detailsFrontToBack(formData),
          },
        },
      });
    }
  };

  const clearErrorsAndSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearErrors();
    handleSubmit(onSubmit)(e);
  };

  return (
    <>
      <PermissionsContext.Provider value={{ editMode: true, responsive: true }}>
        <Form {...form}>
          <form id="yourInformation" onSubmit={clearErrorsAndSubmit}>
            <Card className="container mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Please Tell Us About Yourself</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex flex-col space-y-2">
                    <Input
                      control={control}
                      label="Agency"
                      placeholder="Type here"
                      name="agencyName"
                      orientation="horizontal"
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                      readOnly
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={selectableStates}
                    control={control}
                    name="policyContractState"
                    placeholder="Select an Option"
                    label="Primary Practice State"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>

                <div className="mb-4">
                  <Input
                    control={control}
                    label="First Name"
                    placeholder="Type here"
                    name="firstName"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim={true}
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Middle Name{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="middleName"
                    orientation="horizontal"
                    required={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim={true}
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Last Name"
                    placeholder="Type here"
                    name="lastName"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    trim={true}
                  />
                </div>
                <div className="mb-4 flex w-full flex-row gap-4 font-normal max-md:flex-col">
                  <label className="w-1/2 text-sm font-semibold">
                    <span
                      className={`font-semibold ${errors.professionalDesignation ? 'text-red-500' : ''}`}
                    >
                      Designations
                      <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <div className="flex flex-row flex-wrap md:w-1/2">
                    <Checkbox
                      control={control}
                      label="MD"
                      name="professionalDesignation.MD"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="DDS"
                      name="professionalDesignation.DDS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="DMD"
                      name="professionalDesignation.DMD"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="BDS"
                      name="professionalDesignation.BDS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="MS"
                      name="professionalDesignation.MS"
                      direction="reverse"
                    />
                    <Checkbox
                      control={control}
                      label="MSD"
                      name="professionalDesignation.MSD"
                      direction="reverse"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <DateField
                    control={control}
                    name="dateOfBirth"
                    label="Date of Birth"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Email Address - Personal Email Address"
                    placeholder="Type here"
                    name="emailAddress"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    type="email"
                    pattern=".+@.+\..+"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Email Address (post graduation) (if different then
                        above){' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="postGraduationEmail"
                    orientation="horizontal"
                    required={false}
                    nativeRequired={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                    type="email"
                    pattern=".+@.+\..+"
                  />
                </div>
                <div className="mb-4 flex flex-col space-y-2">
                  <PhoneInput
                    control={control}
                    label="Cell Phone Number"
                    placeholder="Type here"
                    name="cellPhoneNumber"
                    orientation="horizontal"
                    type="tel"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <GoogleInput
                    asSearchField
                    defaultValue=""
                    onSelectPlace={(e) => {
                      const line1: Array<string | undefined> = [
                        undefined,
                        undefined,
                      ];

                      e.address_components?.forEach(
                        ({ short_name, long_name, types }) => {
                          if (types.includes('street_number')) {
                            line1[0] = long_name;
                          }

                          if (types.includes('route')) {
                            line1[1] = long_name;
                          }

                          if (types.includes('subpremise')) {
                            setValue('mailingAddress.line2', long_name);
                          }

                          if (types.includes('postal_code')) {
                            setValue('mailingAddress.zipCode', long_name, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            });
                          }

                          if (types.includes('administrative_area_level_1')) {
                            setValue(
                              'mailingAddress.state',
                              short_name as UsStateEnum,
                              {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              }
                            );
                          }

                          if (
                            types.includes('country') &&
                            (short_name == 'PR' || short_name == 'VI')
                          ) {
                            // Puerto Rico & Virgin Islands are considered as separate countries & not
                            // administrative levels. All territories will need to use this field

                            setValue(
                              'mailingAddress.state',
                              short_name as UsStateEnum,
                              {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              }
                            );
                          }

                          if (types.includes('locality')) {
                            setValue('mailingAddress.city', long_name, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            });
                          }
                        }
                      );

                      setValue(
                        'mailingAddress.line1',
                        line1.filter((pred) => pred != undefined).join(' '),
                        {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        }
                      );
                      setValue('mailingDisplayAddress', e.formatted_address!, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    label={
                      <>
                        Search For Mailing Address{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type to search"
                    orientation="horizontal"
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/2">
                    <Input
                      control={control}
                      label="Street Address"
                      placeholder="Type here"
                      name="mailingAddress.line1"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/2">
                    <Input
                      control={control}
                      label={
                        <>
                          Suite, Floor, Building{' '}
                          <span className="text-brand-400">(Optional)</span>
                        </>
                      }
                      placeholder="Type here"
                      name="mailingAddress.line2"
                      orientation="vertical"
                      required={false}
                      nativeRequired={false}
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/3">
                    <Input
                      control={control}
                      label="City"
                      placeholder="Type here"
                      name="mailingAddress.city"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/3">
                    <Select
                      options={SelectableUnitedStates}
                      control={control}
                      name="mailingAddress.state"
                      placeholder="Select an Option"
                      label="State"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/3">
                    <ZipInput
                      control={control}
                      label="Zip Code"
                      placeholder="Type here"
                      name="mailingAddress.zipCode"
                      orientation="vertical"
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={SelectableYesNo}
                    control={control}
                    name="memberOfSda"
                    placeholder="Select an Option"
                    label="Are you a member of your state dental association or society?"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Name of dental school you graduated from or will graduate from?"
                    placeholder="Type here"
                    name="nameOfDentalSchool"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-1/2 font-semibold max-md:hidden">
                    Month/Year of Graduation
                    <span className="text-red-400">*</span>
                  </div>
                  <div className="flex w-full gap-4 max-md:flex-col md:w-1/2">
                    <div className="mb-4 md:w-1/2">
                      <Select
                        options={selectableMonth}
                        control={control}
                        name="yearMonthMonth"
                        placeholder="Select an Option"
                        label="Month of Graduation"
                        orientation="vertical"
                        required
                        overwriteLabelWidth="w-1/2"
                        overwriteInputWidth="w-1/2"
                      />
                    </div>
                    <div className="mb-4 md:w-1/2">
                      <Input
                        control={control}
                        label="Year of Graduation"
                        placeholder="Type here"
                        name="yearMonthYear"
                        orientation="vertical"
                        required
                        overwriteLabelWidth="w-1/2"
                        overwriteInputWidth="w-1/2"
                        type="number"
                        pattern="^\d{4}$"
                        min={new Date().getFullYear() - 2}
                        max={new Date().getFullYear() + 2}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <Select
                    options={SelectablePracticeSpecialty}
                    control={control}
                    name="practiceSpecialty"
                    placeholder="Select an Option"
                    label="Specialty"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="container mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Primary Practice Location (If Unsure, Enter Your Address In
                  The State Where You Will Primarily Practice)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <GoogleInput
                    asSearchField
                    defaultValue=""
                    onSelectPlace={handleSelectPracticeState}
                    label={
                      <>
                        Search For Primary Practice Address{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type to search"
                    orientation="horizontal"
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label="Street Address"
                    placeholder="Type here"
                    name="primaryPracticeAddress.line1"
                    orientation="vertical"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4">
                  <Input
                    control={control}
                    label={
                      <>
                        Suite, Floor, Building{' '}
                        <span className="text-brand-400">(Optional)</span>
                      </>
                    }
                    placeholder="Type here"
                    name="primaryPracticeAddress.line2"
                    orientation="vertical"
                    required={false}
                    nativeRequired={false}
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
                <div className="mb-4 flex gap-4 max-md:flex-col">
                  <div className="md:w-1/4">
                    <Input
                      control={control}
                      label="City"
                      placeholder="Type here"
                      name="primaryPracticeAddress.city"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/4">
                    <Select
                      options={SelectableUnitedStates}
                      control={control}
                      name="primaryPracticeAddress.state"
                      placeholder="Select an Option"
                      label="State"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/4">
                    <Input
                      control={control}
                      name="primaryPracticeAddress.county"
                      placeholder="Type here"
                      label="County"
                      orientation="vertical"
                      required
                      overwriteLabelWidth="w-1/2"
                      overwriteInputWidth="w-1/2"
                    />
                  </div>
                  <div className="md:w-1/4">
                    <ZipInput
                      control={control}
                      label="Zip Code"
                      placeholder="Type here"
                      name="primaryPracticeAddress.zipCode"
                      orientation="vertical"
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="container">
              {watchFirstTimePractice !== YesNoEnum.NO && (
                <div className="bg-[#009BA70D] p-4">
                  <p className="text-[#009BA7]">
                    <Info className="mr-2 inline h-5 w-5" />
                    <span>
                      Coverage is only available for individuals who are now, or
                      will be in the next 6 months, entering private dental
                      practice for the first time.
                    </span>
                  </p>
                </div>
              )}
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Are you entering in practice for the first time?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Select
                    options={SelectableYesNo}
                    control={control}
                    name="firstTimePractice"
                    placeholder="Select an Option"
                    label="Are you entering in practice for the first time?"
                    orientation="horizontal"
                    required
                    overwriteLabelWidth="w-1/2"
                    overwriteInputWidth="w-1/2"
                  />
                </div>
              </CardContent>
              {watchFirstTimePractice === YesNoEnum.NO && (
                <div className="bg-[#CA4F500D] p-4">
                  <p className="text-[#CA4F50]">
                    <Info className="mr-2 inline h-5 w-5" />
                    <span>
                      Sorry for the confusion, but insurance is only available
                      to individuals who are now or in the next 6 months
                      starting a private dental practice for the first time.
                      Hope to see you soon as we are working and expanding our
                      services to cover all your needs.
                    </span>
                  </p>
                </div>
              )}
            </Card>
          </form>
        </Form>
        <div className="mt-4 flex items-center justify-end">
          <Button
            type="submit"
            form="yourInformation"
            disabled={watchFirstTimePractice === YesNoEnum.NO}
            className={`rounded-md bg-brand-green px-4 py-2 text-white hover:bg-brand-600 ${watchFirstTimePractice === YesNoEnum.NO ? 'cursor-not-allowed opacity-50' : ''}`}
            loading={saveAnswers.isPending}
          >
            Next
          </Button>
        </div>
      </PermissionsContext.Provider>
    </>
  );
};

export default NewGrad;
